import { Input as AntInput, Modal, Select as AntSelect, Table } from "antd";
import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import Input, { InputType } from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import React, { useEffect, useMemo, useState } from "react";
import {
  useCreateHeatpumpDocMutation,
  useCreateHeatpumpMutation,
  useDeleteHeatpumpDocMutation,
  useGetEnvironnementsQuery,
  useUpdateHeatpumpDocMutation,
  useUpdateHeatpumpMutation,
} from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type TableRecord = Record<string, any>;

type HpDocRecord = {
  id?: number | string;
  hp?: number | string;
  title?: string;
  link?: string;
  category?: string;
  environnement_id?: number | string;
};

type EnvironmentOption = {
  value: string;
  code: string;
  label: string;
};

type HpSuperEditPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  mode?: "create" | "edit";
  row?: TableRecord | null;
  envNumeric?: number;
  lang: string;
  onSaved?: () => Promise<void> | void;
};

const HP_DOC_CATEGORY_OPTIONS = [
  { value: ".pdf", label: ".pdf" },
  { value: ".odt", label: ".odt" },
];

function toCheckedEnvMap(row: TableRecord | null | undefined, environments: EnvironmentOption[]) {
  const selected = row?.envs;
  let values: string[] = [];

  if (Array.isArray(selected)) {
    values = selected
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "object") {
          const obj = item as Record<string, unknown>;
          const raw = obj.value ?? obj.id ?? obj.code;
          return typeof raw === "string" || typeof raw === "number" ? String(raw) : "";
        }
        return String(item);
      })
      .map((item) => item.trim())
      .filter(Boolean);
  } else if (typeof selected === "string") {
    values = selected.split(",").map((item) => item.trim()).filter(Boolean);
  }

  return environments.reduce<Record<string, boolean>>((acc, env) => {
    acc[env.code] = values.includes(env.value) || values.includes(env.code);
    return acc;
  }, {});
}

function getCheckedEnvIds(stateEnvs: Record<string, boolean>, environments: EnvironmentOption[]) {
  return environments
    .filter((env) => stateEnvs[env.code])
    .map((env) => env.value);
}

const HpSuperEditPopup: React.FC<HpSuperEditPopupProps> = ({
  isOpen,
  onClose,
  request,
  mode = "edit",
  row,
  envNumeric,
  lang,
  onSaved,
}) => {
  const { t } = useTranslation();
  const isEditMode = mode === "edit";
  const [isSaving, setIsSaving] = useState(false);
  const [hpDocs, setHpDocs] = useState<HpDocRecord[]>([]);
  const [isHpDocModalOpen, setIsHpDocModalOpen] = useState(false);
  const [currentHpDoc, setCurrentHpDoc] = useState<HpDocRecord | null>(null);
  const [hpDocErrors, setHpDocErrors] = useState<{ title?: string; link?: string; category?: string }>({});
  const [envChecks, setEnvChecks] = useState<Record<string, boolean>>({});
  const { data: rawEnvironments = [] } = useGetEnvironnementsQuery("");
  const environments = useMemo<EnvironmentOption[]>(() => {
    return (Array.isArray(rawEnvironments) ? rawEnvironments : []).map((env: any) => ({
      value: String(env.value ?? env.id ?? ""),
      code: String(env.code ?? ""),
      label: String(env.label ?? env.country ?? env.text ?? env.code ?? env.value ?? env.id ?? ""),
    }));
  }, [rawEnvironments]);
  const [createHeatpump] = useCreateHeatpumpMutation();
  const [updateHeatpump] = useUpdateHeatpumpMutation();
  const [createHeatpumpDoc, { isLoading: isCreatingHpDoc }] = useCreateHeatpumpDocMutation();
  const [updateHeatpumpDoc, { isLoading: isUpdatingHpDoc }] = useUpdateHeatpumpDocMutation();
  const [deleteHeatpumpDoc, { isLoading: isDeletingHpDoc }] = useDeleteHeatpumpDocMutation();

  const { control, reset, getValues } = useCustomForm<{
    reference_def: string;
    code: string;
    photo: string;
    classe_energie: string;
    code_schema: string;
    gamme: string;
  }>({
    defaultValues: {
      reference_def: "",
      code: "",
      photo: "",
      classe_energie: "",
      code_schema: "",
      gamme: "",
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    setIsSaving(false);
    setHpDocErrors({});
    reset({
      reference_def: String(row?.reference_def ?? ""),
      code: String(row?.code ?? ""),
      photo: String(row?.photo ?? ""),
      classe_energie: String(row?.classe_energie ?? ""),
      code_schema: String(row?.code_schema ?? ""),
      gamme: String(row?.gamme ?? ""),
    });
    setHpDocs(Array.isArray(row?.hpdocs) ? row.hpdocs : []);
    setEnvChecks(toCheckedEnvMap(row, environments));
  }, [environments, isOpen, reset, row]);

  const openCreateHpDoc = () => {
    const rowId = row?.id ?? row?.key;
    setHpDocErrors({});
    setCurrentHpDoc({ hp: rowId });
    setIsHpDocModalOpen(true);
  };

  const openEditHpDoc = (doc: HpDocRecord) => {
    setHpDocErrors({});
    setCurrentHpDoc({ ...doc });
    setIsHpDocModalOpen(true);
  };

  const closeHpDocModal = () => {
    setIsHpDocModalOpen(false);
    setCurrentHpDoc(null);
    setHpDocErrors({});
  };

  const handleDeleteHpDoc = async (docId: number | string | undefined) => {
    if (docId == null) return;
    try {
      await deleteHeatpumpDoc({ id: docId }).unwrap();
      setHpDocs((prev) => prev.filter((doc) => String(doc.id) !== String(docId)));
    } catch {
      // handled by api interceptor
    }
  };

  const handleHpDocSubmit = async () => {
    if (!currentHpDoc) return;

    const title = String(currentHpDoc.title ?? "").trim();
    const link = String(currentHpDoc.link ?? "").trim();
    const category = String(currentHpDoc.category ?? "").trim();
    const nextErrors: { title?: string; link?: string; category?: string } = {};
    if (!title) nextErrors.title = t("TITLE_ERROR");
    if (!link) nextErrors.link = t("LINK_ERROR");
    if (!category) nextErrors.category = t("TYPE_ERROR");
    setHpDocErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload: Record<string, unknown> = {
      ...currentHpDoc,
      title,
      link,
      category,
      lang,
    };

    try {
      if (currentHpDoc.id == null) {
        const rowId = row?.id ?? row?.key;
        if (rowId == null) return;
        payload.env = envNumeric;
        const created = await createHeatpumpDoc({ heatpumpId: rowId, data: payload }).unwrap();
        setHpDocs((prev) => [...prev, created as HpDocRecord]);
      } else {
        payload.env = currentHpDoc.environnement_id ?? envNumeric;
        const updated = await updateHeatpumpDoc({ id: currentHpDoc.id, data: payload }).unwrap();
        setHpDocs((prev) => {
          const filtered = prev.filter((doc) => String(doc.id) !== String(currentHpDoc.id));
          return [...filtered, updated as HpDocRecord];
        });
      }
      closeHpDocModal();
    } catch {
      // handled by api interceptor
    }
  };

  const handleSave = async () => {
    if (!request) return;
    const rowId = row?.id ?? row?.key;
    if (isEditMode && rowId == null) return;

    const { reference_def, code, photo, classe_energie, code_schema, gamme } = getValues();
    const checkedEnvs = getCheckedEnvIds(envChecks, environments);
    const countries = environments.filter((env) => envChecks[env.code]).map((env) => env.code).join(", ");

    const baseUrl = request.url.replace(/\/[^/]+$/, "");
    const url = isEditMode ? `${baseUrl}/${rowId}` : baseUrl;

    const payload: Record<string, unknown> = {
      id: isEditMode ? rowId : null,
      code,
      reference_def,
      lang,
      env: envNumeric,
      envs: checkedEnvs,
      photo,
      classe_energie,
      code_schema,
      gamme,
      countries,
    };

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateHeatpump({ url, data: payload }).unwrap();
      } else {
        await createHeatpump({ url, data: payload }).unwrap();
      }
      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={t("Infos")}
      onClose={onClose}
      portal
      fixSize="large"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group data-direction="column" data-gap="20" data-space="40" data-space-horizontal="40" data-space-top="5" data-height="autofit">
        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("reference")} name="reference_def" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("code")} name="code" control={control} type="text" size="large" required />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("photo")} name="photo" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("classe_energie")} name="classe_energie" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("code_schema")} name="code_schema" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("gamme")} name="gamme" control={control} type="text" size="large" />
          </group>
        </group>
        {isEditMode && (
          <group data-direction="column" data-gap="8" data-space-top="10">
            {/* <text data-weight="700">{t("Heatpump documents")}</text>
            <group data-justify="left" data-gap="10">
              <Button type="button" icon="add" onClick={openCreateHpDoc} />
            </group> */}

            <group data-justify="space-between" data-align="center" data-gap="10">
                <text data-weight="700">{t("Heatpump documents")}</text>
                <div //NOSONAR - intentional div-based icon control per project UX pattern
                    className="button primary"
                    role="button"
                    tabIndex={0}
                    aria-label="Add"
                    onClick={openCreateHpDoc}
                    onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openCreateHpDoc();
                    }}
                >
                    <icon>add</icon>
                </div>
            </group>

            <Table
              size="small"
              pagination={false}
              dataSource={hpDocs.map((doc, index) => ({
                key: String(doc.id ?? `new-${index}`),
                ...doc,
              }))}
              columns={[
                { title: t("Title"), dataIndex: "title", key: "title" },
                { title: t("Link"), dataIndex: "link", key: "link" },
                { title: t("Type"), dataIndex: "category", key: "category", width: 120 },
                {
                  title: t("Action"),
                  key: "action",
                  width: 120,
                  render: (_: unknown, record: HpDocRecord) => (
                    <group data-wrap="no" data-gap="10">
                      <Button type="button" mini onClick={() => openEditHpDoc(record)}>
                        <icon>edit</icon>
                      </Button>
                      <Button
                        type="button"
                        mini
                        {...{ "data-color": "error-color" }}
                        onClick={() => handleDeleteHpDoc(record.id)}
                        disabled={isDeletingHpDoc}
                      >
                        <icon>delete</icon>
                      </Button>
                    </group>
                  ),
                },
              ]}
              locale={{ emptyText: t("No documents") }}
            />
          </group>
        )}

        <group data-direction="column" data-gap="8" data-space-top="10">
          <text data-weight="700">{t("environnement_mgt")}</text>
          <group data-gap="20" data-wrap="wrap">
            {environments.length === 0 && <text>{t("No data")}</text>}
            {environments.map((env, index) => (
              <group key={`${env.code}-${env.value}-${index}`} data-width="auto">
                <Input
                  id={`hp-super-env-${env.code}-${index}`}
                  type={InputType.Checkbox}
                  name={`env_${env.value}`}
                  label={env.label}
                  checked={Boolean(envChecks[env.code])}
                  onCustomChange={(_name, checked) => {
                    setEnvChecks((prev) => ({ ...prev, [env.code]: Boolean(checked) }));
                  }}
                />
              </group>
            ))}
          </group>
        </group>
      </group>

      <Modal
        title={currentHpDoc?.id == null ? t("Add") : t("Edit")}
        open={isHpDocModalOpen}
        onCancel={closeHpDocModal}
        onOk={handleHpDocSubmit}
        okText={t("Done")}
        cancelText={t("Back")}
        confirmLoading={isCreatingHpDoc || isUpdatingHpDoc}
      >
        <group data-direction="column" data-gap="12">
          <group data-direction="column" data-gap="4">
            <text>{t("Title")}</text>
            <AntInput
              value={currentHpDoc?.title ?? ""}
              onChange={(e) =>
                setCurrentHpDoc((prev) => ({
                  id: prev?.id,
                  hp: prev?.hp,
                  title: e.target.value,
                  link: prev?.link,
                  category: prev?.category,
                  environnement_id: prev?.environnement_id,
                }))
              }
            />
            {hpDocErrors.title && <text data-color="error-color">{hpDocErrors.title}</text>}
          </group>

          <group data-direction="column" data-gap="4">
            <text>{t("Link")}</text>
            <AntInput
              value={currentHpDoc?.link ?? ""}
              onChange={(e) =>
                setCurrentHpDoc((prev) => ({
                  id: prev?.id,
                  hp: prev?.hp,
                  title: prev?.title,
                  link: e.target.value,
                  category: prev?.category,
                  environnement_id: prev?.environnement_id,
                }))
              }
            />
            {hpDocErrors.link && <text data-color="error-color">{hpDocErrors.link}</text>}
          </group>

          <group data-direction="column" data-gap="4">
            <text>{t("Category")}</text>
            <AntSelect
              value={currentHpDoc?.category}
              options={HP_DOC_CATEGORY_OPTIONS}
              onChange={(value) =>
                setCurrentHpDoc((prev) => ({
                  id: prev?.id,
                  hp: prev?.hp,
                  title: prev?.title,
                  link: prev?.link,
                  category: String(value),
                  environnement_id: prev?.environnement_id,
                }))
              }
            />
            {hpDocErrors.category && <text data-color="error-color">{hpDocErrors.category}</text>}
          </group>
        </group>
      </Modal>
    </Popup>
  );
};

export default HpSuperEditPopup;